import { forwardRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { S } from './style';
import { useResize } from '@algoan/dashboard/utils';
import { useTransactionCategoryTranslator } from '../../messages';
import { getDueDayFromRegularCashFlow } from './timeline-utils';
import { RegularCashFlow } from './timeline-model';

export const firstDayOffset = 0;

const maxLevel = 50;

export const getDueDayOnTimeline = (day: number, dueDays: { [key: number]: number }): number => {
  return dueDays[day] + 1;
};

export const Event = forwardRef<
  HTMLDivElement,
  {
    daySpacing: number;
    day: number;
    dueDay: number;
    positive: boolean;
    label: string;
    level: number;
    width: number;
    sameDay: boolean;
  }
>(({ daySpacing, day, dueDay, positive, label, level, width, sameDay }, ref) => {
  return (
    <S.Event ref={ref} daySpacing={daySpacing} day={firstDayOffset + dueDay - 1} elementWidth={width}>
      <S.EventLabel title={label}>{label}</S.EventLabel>
      {!sameDay && (
        <S.EventSpacer level={level}>
          {Array(Math.max(level * 2 - 1, 0))
            .fill(0)
            .map((_value, index) => (
              <S.EventDot key={index} positive={positive} />
            ))}
        </S.EventSpacer>
      )}
      <S.EventTick positive={positive} level={sameDay ? level + 1 : 0} />
      {day !== 15 && day !== 1 && !sameDay && (
        <S.EventDate>
          <FormattedMessage
            id="widgets.regular_cash_flows_analysis.timeline.date_tick"
            description="Date tick"
            defaultMessage="{day}th"
            values={{
              day: day < 0 ? 31 + day : day,
            }}
          />
        </S.EventDate>
      )}
    </S.Event>
  );
});
Event.displayName = 'Event';

export const Events: React.FC<{
  cashFlows: RegularCashFlow[];
  daySpacing: number;
  dueDays: { [key: number]: number };
}> = ({ cashFlows, daySpacing, dueDays }) => {
  const translateTransactionCategory = useTransactionCategoryTranslator();

  // array of element sizes sorted by level
  const elementSizesByLevel: Array<Array<{ left: number; right: number }>> = [];

  cashFlows.sort((cashFlowA, cashFlowB) => {
    const dueDayOnTimelineA = getDueDayOnTimeline(getDueDayFromRegularCashFlow(cashFlowA), dueDays);
    const dueDayOnTimelineB = getDueDayOnTimeline(getDueDayFromRegularCashFlow(cashFlowB), dueDays);

    // if cash flow are on the same day, we sort them by length
    if (dueDayOnTimelineA === dueDayOnTimelineB) {
      const labelA = cashFlowA.label ?? translateTransactionCategory(cashFlowA.category);
      const labelB = cashFlowB.label ?? translateTransactionCategory(cashFlowB.category);

      return labelA.length - labelB.length;
    }

    return dueDayOnTimelineA - dueDayOnTimelineB;
  });

  // Calculate for each Event component the level
  // Use the elementSizesByLevel to prevent overlapping
  const refs = cashFlows.map((cashFlow, index) => {
    let level: number;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { width, elementRef } = useResize();

    const dueDay = getDueDayOnTimeline(getDueDayFromRegularCashFlow(cashFlow), dueDays);
    const dayHasAlreadyAnotherEvent =
      index > 0 && getDueDayFromRegularCashFlow(cashFlows[index - 1]) === getDueDayFromRegularCashFlow(cashFlow);

    const left = daySpacing * dueDay - width / 2;
    const right = left + width;

    for (level = 0; level < maxLevel; level++) {
      if (typeof elementSizesByLevel[level] === 'undefined') {
        elementSizesByLevel[level] = [];
      }

      if (
        elementSizesByLevel[level].length === 0 ||
        elementSizesByLevel[level].every(
          (elementPosition) => left > elementPosition.right && right > elementPosition.right,
        )
      ) {
        elementSizesByLevel[level].push({ left, right });

        // we reserve two levels when events are superimposed
        if (dayHasAlreadyAnotherEvent) {
          if (typeof elementSizesByLevel[level + 1] === 'undefined') {
            elementSizesByLevel[level + 1] = [];
          }

          elementSizesByLevel[level + 1].push({ left, right });
        }

        break;
      }
    }

    return { width, elementRef, level, dayHasAlreadyAnotherEvent, dueDay };
  });

  return (
    <S.Events levels={elementSizesByLevel.length}>
      {cashFlows.map((cashFlow, index) => {
        return (
          <Event
            ref={refs[index].elementRef}
            width={refs[index].width}
            key={index}
            daySpacing={daySpacing}
            day={getDueDayFromRegularCashFlow(cashFlow)}
            dueDay={refs[index].dueDay}
            positive={cashFlow.totalAmount >= 0}
            label={cashFlow.label ?? translateTransactionCategory(cashFlow.category)}
            level={refs[index].level}
            sameDay={refs[index].dayHasAlreadyAnotherEvent}
          />
        );
      })}
    </S.Events>
  );
};
